import React, { useEffect, useState } from 'react';
import { Box, Alert, Typography, Button, CircularProgress } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { getContainerUrl } from 'services/containerDirectory';
import { useSignOut } from 'hooks/useSignOut';
import { useTranslation } from 'react-i18next';

/**
 * Shown to a portal user (end_user / supervisor) who reached the admin app
 * while it runs as the control plane: the portal no longer lives here, it is
 * served by the user's own tenant container. Resolves that container's
 * address from tenant status and offers the link (old bookmarks and emailed
 * links land here during the migration).
 *
 * Names the signed-in account and offers a sign-out: a staff member whose
 * browser still holds a customer's session would otherwise be stuck here,
 * since the admin header (and its logout) is not rendered.
 */
const PortalMovedNotice: React.FC = () => {
  const { t } = useTranslation('auth');
  const { user } = useAuth();
  const signOut = useSignOut();
  const tenantId = user?.tenant_roles?.[0]?.tenant_id ?? null;
  const [portalUrl, setPortalUrl] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let active = true;
    if (!tenantId) {
      setPortalUrl(null);
      return;
    }
    getContainerUrl(tenantId).then((origin) => {
      if (active) setPortalUrl(origin ? `${origin}/portal` : null);
    });
    return () => {
      active = false;
    };
  }, [tenantId]);

  return (
    <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
      <Alert severity="info" sx={{ maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom>
          {t('portalMoved.title')}
        </Typography>
        {user?.username && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('portalMoved.signedInAs', { username: user.username })}
          </Typography>
        )}
        {portalUrl === undefined && <CircularProgress size={20} />}
        {portalUrl && (
          <>
            <Typography>{t('portalMoved.movedTo')}</Typography>
            <Typography gutterBottom>
              <a href={portalUrl}>{portalUrl}</a>
            </Typography>
            <Button variant="contained" size="small" href={portalUrl}>
              {t('portalMoved.open')}
            </Button>
          </>
        )}
        {portalUrl === null && (
          <Typography>{t('portalMoved.noAddress')}</Typography>
        )}
        <Box sx={{ mt: 2 }}>
          <Button variant="text" size="small" onClick={signOut}>
            {t('session.signOut')}
          </Button>
        </Box>
      </Alert>
    </Box>
  );
};

export default PortalMovedNotice;
